import { memo, useEffect, useRef, useState } from "react";
import CardHeader, { type CardHeaderProps } from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import Divider from "@mui/material/Divider";
import Button, { type ButtonProps } from "@mui/material/Button";
import Badge from "@mui/material/Badge";
import Chip from "@mui/material/Chip";
import { Link } from "infrastructure/router";
import type { WithSlots } from "types";
import { OnboardingApplicationStatus, onboardingApplicationStatus } from "consts/onboarding";
import { getPromiseSettledResourceResult } from "util/resource";
import { ApplicationsResponse } from "features/onboarding/types";
import Card from "ui/atoms/Card";
import When from "ui/support/When";
import type { ApplicationSummaryProps } from "./types";
import useApplicationSummary from "./useApplicationSummary";

type Props = WithSlots<
    ApplicationSummaryProps,
    {
        readonly header: CardHeaderProps;
        readonly link: ButtonProps;
    }
>;

const ApplicationSummary = ({ slotProps, ...restProps }: Props) => {
    const [[
        awaitingReviewApplications,
        requestedChangesApplications,
        inProgressApplications,
        approvedApplications
    ], setState] = useState<ApplicationsResponse[]>([]);
    const { fetch, isLoading } = useApplicationSummary(restProps);
    const fetchRef = useRef(fetch);
    fetchRef.current = fetch;

    useEffect(() => {
        fetchRef.current('')
            .then(results => setState(state => [
                ...state,
                ...results.map(getPromiseSettledResourceResult)
                    .filter(Boolean) as ApplicationsResponse[]
            ]));
    }, []);

    const { href, ...restButtonProps } = slotProps?.link || {};

    return (
        <Card>
            <CardHeader
                {...slotProps?.header}
            />
            <CardContent>
                <Divider />
                <Box
                    sx={{
                        py: 2
                    }}
                >
                    <When
                        if={isLoading}
                        then={(
                            <>
                                <Skeleton />
                                <Skeleton />
                                <Skeleton />
                                <Skeleton />
                            </>
                        )}
                    >
                        <Box
                            sx={{
                                display: 'grid',
                                gap: 2
                            }}
                        >
                            {[
                                {
                                    status: OnboardingApplicationStatus.AwaitingReview,
                                    data: awaitingReviewApplications
                                },
                                {
                                    status: OnboardingApplicationStatus.RequestedChanges,
                                    data: requestedChangesApplications
                                },
                                {
                                    status: OnboardingApplicationStatus.InProgress,
                                    data: inProgressApplications
                                },
                                {
                                    status: OnboardingApplicationStatus.Approved,
                                    data: approvedApplications
                                }
                            ].map(({ status, data }) => {
                                const {
                                    label,
                                    color
                                } = onboardingApplicationStatus.get(status) ?? {};

                                return (
                                    <Box
                                        key={status}
                                    >
                                        <Badge
                                            color='error'
                                            badgeContent={data?.data.total || '0'}
                                            sx={{
                                                right: -16
                                            }}
                                        >
                                            <Chip
                                                color={color}
                                                label={label}
                                                size='medium'
                                            />
                                        </Badge>
                                    </Box>
                                );
                            })}
                        </Box>
                    </When>
                </Box>
                <Divider />
            </CardContent>
            <CardActions
                sx={{
                    display: 'flex',
                    justifyContent: 'center'
                }}
            >
                <Link
                    to={String(href)}
                >
                    <Button
                        {...restButtonProps}
                    />
                </Link>
            </CardActions>
        </Card>
    );
};

export default memo(ApplicationSummary);
